import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { shadowLight } from 'app/colors';
import {
  KizenTypography,
  textCss,
  truncateCss,
  fontSizes,
  fontWeights,
} from 'app/typography';
import { borderRadii, layers } from 'app/spacing';
import DraggableItem from 'components/Kizen/DraggableItem';
import Icon from 'components/Kizen/Icon';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import { FIELD_MARGIN } from './constants';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import EditableText from 'components/Kizen/EditableText';
import IconButton from 'components/Kizen/IconButton';
import Select from 'components/Inputs/Select';

export const StyledFieldWrapper = styled(DraggableItem)`
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 10px;
  margin: ${FIELD_MARGIN}px;
  border-radius: ${borderRadii.standard};
  overflow: hidden;

  && {
    ${({ dashedBorder = false }) =>
      dashedBorder
        ? css`
            border: unset;
          `
        : ''}
  }

  ${({ dragging }) => dragging && shadowLight}
  ${({ bringToTop, forceToTop }) =>
    (bringToTop || forceToTop) &&
    css`
      z-index: ${layers.content(0, 1)};
    `}
  .FieldHandle {
    cursor: grab;
    margin-right: 10px;
  }
  && .FieldHandle:hover svg {
    color: var(--color-background-darker);
  }
  &:hover .FieldHandle svg {
    color: var(--color-background-dark);
  }
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: var(--color-background-light);
      && .FieldHandle,
      && .FieldHandle:hover {
        cursor: default;
        svg {
          color: var(--color-background-medium);
        }
      }
    `}
`;

export const FieldLayout = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  // Input now Text Wrapper

  // Icon wrapper
  > *:last-child {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    > * {
      margin-left: 10px;
    }
  }
`;

export const TextWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;

  > div {
    // Naturally truncate if the container is sized
    max-width: 100%;
    ${truncateCss}
    input[disabled] {
      background: none;
    }
  }
`;

export const FieldPlaceholder = styled.div`
  position: absolute;
  top: ${FIELD_MARGIN}px;
  bottom: ${FIELD_MARGIN}px;
  left: ${FIELD_MARGIN}px;
  right: ${FIELD_MARGIN}px;
  background-color: var(--color-background-light);
  border: 1px dashed var(--color-background-medium);
  border-radius: ${borderRadii.standard};
`;

export const NewFieldTypography = styled(KizenTypography)`
  align-self: flex-start;
  margin-bottom: 20px;
  margin-left: 20px;
  cursor: pointer;
  color: var(--color-button-primary-default);
  &:hover {
    color: var(--color-button-primary-hover);
  }
`;

export const FieldNameIcon = styled(IconWithTooltip)`
  cursor: pointer;

  && svg {
    width: ${({ width = 11.25 }) => `${width}px`};
    height: ${({ height = 15 }) => `${height}px`};
  }
`;

export const VisibilityRuleIcon = styled(FieldNameIcon)`
  && svg {
    color: var(--color-button-secondary-default);

    :hover {
      color: var(--color-button-secondary-hover);
    }
  }
`;

export const RequiredAsterisk = styled.div`
  width: 15px;
  text-align: right;
  line-height: 20px;
  ${({ color = 'var(--color-button-warning-hover)' }) =>
    textCss('header', 'regular', 'none', 'none', color)}
  ${({ hasTooltip = false }) => hasTooltip && 'cursor: pointer;'}
`;

export const LeftContainer = styled.div`
  display: flex;
  gap: 10px;
  min-width: 0;
`;

export const StyledReadonlyName = styled(TextEllipsisWithTooltip)`
  padding: 1px 2px 7px;
`;

export const categoryMargin = 10;

export const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: var(--color-background-white);
  border: 1px solid var(--color-background-medium);
  border-radius: ${borderRadii.small};
  margin: ${categoryMargin}px;
  min-width: 0;
  ${({ dragging }) => dragging && shadowLight}
`;

export const CategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  height: 30px;
  margin: 10px;
  margin-bottom: 0;
  // Drag icon
  > *:first-child {
    width: 15px;
    cursor: grab;
  }
  // Delete icon
  > *:last-child {
    width: 15px;
    svg {
      width: 15px;
      height: auto;
    }
  }
`;

export const EditableNameText = styled(EditableText)`
  align-self: flex-end;
  margin: 0 15px;
  min-width: 0;
  max-width: 350px;
  input {
    font-size: ${fontSizes.text};
    font-weight: ${fontWeights.bold};
    &:disabled {
      background: none;
    }
  }
`;

export const DeleteIconButton = styled(IconButton)`
  ${({ visible }) =>
    !visible &&
    css`
      visibility: hidden;
    `}
`;

export const CategoryPlaceholder = styled.div`
  position: absolute;
  top: ${categoryMargin}px;
  bottom: ${categoryMargin}px;
  left: ${categoryMargin}px;
  right: ${categoryMargin}px;
  background-color: var(--color-background-light);
  border: 1px dashed var(--color-background-medium);
  border-radius: ${borderRadii.small};
`;

export const NewCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 113px;
  align-items: center;
  margin: ${categoryMargin}px;
  border: 1px dashed var(--color-background-medium);
  border-radius: ${borderRadii.small};
  cursor: pointer;
  > div:first-child {
    margin-top: 20px;
    margin-bottom: 17px;
  }
  color: var(--color-button-primary-default);
  &:hover {
    color: var(--color-button-primary-hover);
  }
`;

export const StyledMigateFieldsToCategorySelect = styled(Select)`
  margin-top: 20px;
  label {
    text-align: left;
  }
`;

export const EditableTextLeftIcon = styled(Icon)`
  color: var(--color-background-darker);
  margin-right: 19px;
  margin-left: 2px;
  min-width: 20px;

  svg {
    width: 20px;
  }

  :hover {
    color: var(--color-background-darker);
  }
`;
